import React, { useEffect, useMemo } from 'react';
import { BackHandler, StyleSheet, View, } from 'react-native';
import Animated, { FadeInDown, FadeInUp, FadeOutDown, FadeOutUp, } from 'react-native-reanimated';
import { rem } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useStatusBarHeight } from 'hooks/useHeaderHeight';
import useIsTablet from 'hooks/useIsTablet';
import useStyles from 'hooks/useStyles';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import createStyleSheets from 'utils/createStyleSheets';
import { isWeb } from '../constants';
// react-native-modal's duration, which this used before.
const DURATION = 300;
export const ToastModal = ({ isVisible, position = 'top', style, onCloseAction, children, }) => {
    const statusBarHeight = useStatusBarHeight();
    const { bottom } = useSafeAreaInsets();
    const isTablet = useIsTablet();
    const modalStyle = useMemo(() => [
        staticStyles.modal,
        staticStyles.modalCentered,
        position === 'top'
            ? {
                height: statusBarHeight + rem(80),
                paddingTop: statusBarHeight,
            }
            : {
                marginBottom: isTablet ? bottom : rem(16),
            },
        style,
    ], [bottom, isTablet, position, statusBarHeight, style]);
    const { width } = useAppFrameDimensions(isWeb);
    useEffect(() => {
        // react-native-web's BackHandler only logs an error: web has no back
        // button to handle.
        if (!isVisible || isWeb) {
            return undefined;
        }
        const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
            onCloseAction();
            return true;
        });
        return () => subscription.remove();
    }, [isVisible, onCloseAction]);
    const styles = useStyles(styleSet);
    // Reanimated names entrances by where they start: FadeInDown starts below and
    // rises, the opposite of react-native-animatable's fadeInUp.
    const entering = (position === 'bottom' ? FadeInDown : FadeInUp).duration(DURATION);
    const exiting = (position === 'bottom' ? FadeOutDown : FadeOutUp).duration(DURATION);
    // Laid over the screen it is rendered in, letting touches through around
    // the toast.
    return (<View pointerEvents="box-none" style={[
            staticStyles.modalOuter,
            position === 'bottom' && staticStyles.modalOuterBottom,
            isTablet && staticStyles.modalOuterTablet,
        ]}>
      {isVisible ? (<Animated.View entering={entering} exiting={exiting} pointerEvents="box-none" style={isTablet ? { width } : undefined}>
          <View style={modalStyle}>
            <View style={[styles.container, styles.containerCentered]}>
              <View style={styles.content}>{children}</View>
            </View>
          </View>
        </Animated.View>) : null}
    </View>);
};
const staticStyles = StyleSheet.create({
    modalOuter: {
        ...StyleSheet.absoluteFillObject,
        justifyContent: 'flex-start',
        zIndex: 2,
    },
    modalOuterBottom: {
        justifyContent: 'flex-end',
    },
    modalOuterTablet: {
        alignItems: 'center',
    },
    modal: {
        backgroundColor: 'transparent',
        paddingHorizontal: rem(16),
    },
    modalCentered: {
        paddingHorizontal: rem(16),
        alignSelf: 'center',
    },
});
const styleSet = createStyleSheets((colors) => ({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'hidden',
        borderRadius: rem(16),
        paddingHorizontal: rem(16),
        paddingVertical: rem(16),
        backgroundColor: colors.background.contrast,
        ...(isWeb && { minWidth: rem(343) }),
    },
    containerCentered: {
        paddingVertical: rem(12),
        paddingHorizontal: rem(16),
    },
    content: {
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'hidden',
        flex: 1,
    },
}));
export default ToastModal;
